import {
    alpha,
    Box,
    Chip,
    Collapse,
    Grid,
    IconButton,
    styled,
    Typography,
} from '@mui/material'
import { ReactNode } from 'react'
import { OrderItemViewModel } from 'store/models/sellModels'
import AssignToChip from './tableService/AssignToChip'
import { useAssignTo } from 'views/Sale/TableService/hooks/useAssignTo'
import { displayCartItemName } from 'components/Sell/utils/common'
import { KitchenStatusIcon } from 'components/Sell/TableLookupDialog/components/KitchenStatusManual'
import { usePosContext } from 'views/Sale/contexts/PosProvider'
import { useTableSaleSelectContext } from 'views/Sale/contexts/TableSaleSelectContext'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { Delete } from '@mui/icons-material'
import { isDraftItem } from 'views/Sale/utils/orderItemUtil'
import useTableLayoutDisplay from 'hooks/useTableLayoutDisplay/useTableLayoutDisplay'
import { excludedFromForwardProps } from 'utilities/styledUtil'

const PriceLineThrough = styled('span')({
    textDecoration: 'line-through',
    marginRight: 2,
})
const AssignCollapse = ({ orderItem }: { orderItem: OrderItemViewModel }) => {
    //can null when call from QS
    const { relatedOrders = [] } = useTableSaleSelectContext()
    const { getAssignDetails } = useAssignTo({ relatedOrders })
    return (
        <Box>
            <Typography fontSize={'12px'}>
                Assign to: {getAssignDetails(orderItem.assignTo ?? [])}
            </Typography>
        </Box>
    )
}
const CartRowDetail = ({
    item,
    children,
    openAssignDetail = false,
    showTableAssignment,
    onRemoveItem,
    isVoidItem = false,
    disableEditOrder = false,
    isReadonly = false,
}: {
    item: OrderItemViewModel
    children: (item: OrderItemViewModel) => ReactNode
    openAssignDetail: boolean
    showTableAssignment: boolean
    onRemoveItem?: (item: OrderItemViewModel) => void
    isVoidItem?: boolean
    disableEditOrder?: boolean
    isReadonly?: boolean
}) => {
    //can null when call from QS
    const { relatedOrders = [] } = useTableSaleSelectContext()

    const { getTagLabel } = useAssignTo({ relatedOrders })
    const { isKDSEnable } = usePosContext()
    const { isMobile } = useScreenSize()
    const { displaySeatNote } = useTableLayoutDisplay()

    return (
        <Grid item container direction="row">
            <Grid item xs={2} lg={1}>
                <Typography sx={{ whiteSpace: 'nowrap' }}>
                    {item.amount}x
                </Typography>
            </Grid>
            <Grid item xs={10} lg={11}>
                <Typography
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        ml: { lg: 1, xl: 0 },
                    }}
                    component="div"
                >
                    <Grid container>
                        <Grid item width={isMobile ? 180 : undefined} xs>
                            <Box component={'span'}>
                                {displayCartItemName(item)}
                                {showTableAssignment ? (
                                    <AssignToChip
                                        tag={getTagLabel(item.assignTo ?? [])}
                                    />
                                ) : isKDSEnable &&
                                  !Boolean(
                                      item.item.baseDepositAndCreditItemId,
                                  ) ? (
                                    <KitchenStatusIcon
                                        kdsStatus={item.kdsStatus}
                                        sx={{
                                            mb: '-3px',
                                            ml: '1px',
                                            height: '18px',
                                        }}
                                    />
                                ) : (
                                    <></>
                                )}
                                {item.seatNote ? (
                                    <Chip
                                        size="small"
                                        label={displaySeatNote(item.seatNote)}
                                        color="primary"
                                    />
                                ) : null}
                                {!isReadonly &&
                                    onRemoveItem &&
                                    isDraftItem(item) && (
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (onRemoveItem)
                                                    onRemoveItem(item)
                                            }}
                                            sx={{ p: 0 }}
                                            disabled={
                                                isVoidItem || disableEditOrder
                                            }
                                        >
                                            <Delete />
                                        </IconButton>
                                    )}
                            </Box>
                        </Grid>
                        <Grid item xs="auto">
                            {children(item)}
                        </Grid>
                    </Grid>
                </Typography>
            </Grid>
            {showTableAssignment && (
                <Collapse in={openAssignDetail}>
                    <AssignCollapse orderItem={item} />
                </Collapse>
            )}
        </Grid>
    )
}

const CartRowGrid = styled(
    Grid,
    excludedFromForwardProps('isSelected', 'isMobile', 'isDisabled'),
)<{
    isSelected?: boolean
    isMobile?: boolean
    isDisabled?: boolean
}>(({ theme, isDisabled, isSelected, isMobile }) => ({
    cursor: 'pointer',
    padding: theme.spacing(isMobile ? 0 : 1),
    '&:hover': {
        backgroundColor: isSelected
            ? undefined
            : alpha(theme.palette.default.main, 0.5),
        color: isSelected ? undefined : theme.palette.default.contrastText,
        '@media (hover: none)': {
            backgroundColor: undefined,
            color: undefined,
        },
    },
    flexWrap: 'nowrap',
    wordWrap: 'break-word',

    ...(isSelected &&
        !isMobile && {
            backgroundColor: theme.palette.default.main,
            color: theme.palette.default.contrastText,
        }),
    ...(isDisabled && {
        color: isSelected
            ? theme.palette.default.contrastText
            : theme.palette.bluegrey.main,
    }),
}))

function ItemCart({
    item,
    onDoubleClick,
    onClick,
    children,
    highlightNewItem,
    isReadonly,
}: {
    item: OrderItemViewModel
    onClick: (item: OrderItemViewModel) => void
    onDoubleClick: (item: OrderItemViewModel) => void
    children: ReactNode
    highlightNewItem?: boolean
    isReadonly?: boolean
}) {
    return (
        <Box
            bgcolor={
                !isReadonly
                    ? highlightNewItem
                        ? item.isSaved
                            ? ''
                            : 'warning.lighter'
                        : ''
                    : ''
            }
            key={item.item.itemId + 'cart'}
            onClick={() => !isReadonly && onClick(item)}
            onDoubleClick={() => !isReadonly && onDoubleClick?.(item)}
            sx={{ pb: 1 }}
        >
            {children}
        </Box>
    )
}

ItemCart.CartDetail = CartRowDetail
ItemCart.PriceLineThrough = PriceLineThrough

ItemCart.Row = CartRowGrid
export default ItemCart
